// WishNew — `/wishes/new` route (W33.3 follow-up + W34.0 polish).
//
// Form per `docs/spec/wishlist/DESIGN.md §8`:
//   - Field: name (text, required, 1-100)
//   - Field: category (chip group, 7 options)
//   - Field: priority (chip group, 3 options)
//   - Field: target_date (date picker, optional)
//   - Field: visibility (toggle shared/private)
//
// Per spec §8.1: "提交后自动按 category 预置 blocks（per §3.4），用户进
// layout editor 自定义。" The backend `service/crud.py::_CATEGORY_DEFAULT_BLOCKS`
// auto-creates the template blocks on `POST /api/v1/wishes`. After submit we
// navigate to `/wishes/:id/edit-layout` so the user can adjust.
//
// Desktop: centered modal (520px) per spec §8.2.
// Mobile: full-screen BottomSheet variant (large detent) per spec §8.2 + the
//   cross-module HIG principle from `../../DESIGN.md §11`.
//
// Touch target ≥ 44pt per Apple HIG (`DESIGN.md §15`); chip group reused from
// the existing FilterChipGroup primitive so the form visually matches filter
// rails elsewhere in the app.

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Sparkles, X } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { SectionStack } from "@/components/layout/SectionStack";
import { PageHero } from "@/components/primitives/PageHero";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { PageBottomActionBar } from "@/components/system/PageBottomActionBar";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { WishFormFields } from "./wishes/WishFormFields";
// W45.0: the label maps, the category→hint copy and the two chip lists moved to
// `./wishes/wishFormCopy`. They are data with no behaviour, and this file was
// already over `route-size-ceiling`'s recorded baseline — `AGENTS.md §3 #10`
// says 「超就当场拆」, and editing the baseline to make a red test green is the
// one option that is not a fix. W49.0 §8 moved the fields themselves out too;
// the copy follows them.
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { toHktToday } from "@/lib/utils/hkt-time";
import {
  createWish,
  type WishCategory,
  type WishCreate,
  type WishPriority,
  type WishVisibility,
} from "@/lib/api/wishlist";
import { ApiError } from "@/lib/api/pair";
import { queryKeys } from "@/lib/queries/keys";

// --- Form state -----------------------------------------------------------

interface FormState {
  name: string;
  category: WishCategory;
  priority: WishPriority;
  targetDate: string;
  visibility: WishVisibility;
}

function defaultState(): FormState {
  return {
    name: "",
    category: "general",
    priority: "want",
    targetDate: "",
    visibility: "shared",
  };
}

// --- Page component -------------------------------------------------------

export function WishNewPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // W35.6: on mobile this page docks its create action, so it owns the bottom
  // edge and the PillNav steps aside (see BottomChrome).
  useHidePillNav();
  // Lets the docked bar drive `<form>` submission, since the bar renders
  // outside the form element.
  const formRef = useRef<HTMLFormElement>(null);

  const [form, setForm] = useState<FormState>(defaultState());
  const [touched, setTouched] = useState<Record<keyof FormState, boolean>>({
    name: false,
    category: false,
    priority: false,
    targetDate: false,
    visibility: false,
  });
  const today = useMemo(() => toHktToday(), []);

  const createMut = useMutation({
    mutationFn: async (payload: WishCreate) => createWish(payload),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "stats"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      // Per DESIGN §8.1 — 提交后自动按 category 预置 blocks，用户进
      // layout editor 自定义. So we redirect to /wishes/:id/edit-layout.
      navigate(`/wishes/${data.id}/edit-layout`, { replace: true });
    },
  });

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setTouched({
        name: true,
        category: true,
        priority: true,
        targetDate: true,
        visibility: true,
      });
      const trimmed = form.name.trim();
      if (!trimmed) return;
      const payload: WishCreate = {
        name: trimmed,
        category: form.category,
        priority: form.priority,
        visibility: form.visibility,
        target_date: form.targetDate ? form.targetDate : null,
      };
      createMut.mutate(payload);
    },
    [form, createMut],
  );

  const nameInvalid = touched.name && form.name.trim().length === 0;
  const dateInvalid = touched.targetDate && form.targetDate.length > 0 && form.targetDate < today;
  const errorMessage =
    createMut.error instanceof ApiError
      ? createMut.error.message
      : createMut.error instanceof Error
        ? createMut.error.message
        : null;

  // The form body is identical for desktop + mobile. The mobile variant
  // wraps it in a full-screen BottomSheet (per spec §8.2) — the desktop
  // path renders inline under PageContainer.
  const body = (
    <form
      ref={formRef}
      data-component="WishNewForm"
      data-testid="wish-new-form"
      onSubmit={onSubmit}
      className="flex flex-col gap-s-4"
      noValidate
    >
      {/* W49.0 §8 — the fields moved to `wishes/WishFormFields` so
          `/wishes/:id/edit` renders the same five. The `<form>` element and the
          action row stay here: what they submit and where they go afterwards
          differ between create and edit. */}
      <WishFormFields
        form={form}
        onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
        nameInvalid={nameInvalid}
        dateInvalid={dateInvalid}
        today={today}
        errorMessage={errorMessage}
        autoFocusName
      />
    </form>
  );

  // W35.6: the create / cancel row is a docked `PageBottomActionBar` on
  // mobile (so it owns the bottom edge and the PillNav steps aside) and stays
  // inline on desktop, where a form-scoped submit row is the native pattern.
  // Extracted so both viewports render the same handlers, not the same chrome.
  const formActions = (
    <div
      className="flex items-center justify-end gap-s-2 pt-s-2 border-t border-hairline"
      data-component="WishNewFormActions"
    >
      <button
        type="button"
        onClick={() => navigate("/wishes")}
        data-testid="wish-new-cancel"
        className="press inline-flex items-center gap-1.5 min-h-11 px-s-4 rounded-full border border-hairline text-ink-1"
      >
        <X className="h-4 w-4" aria-hidden />
        取消
      </button>
      <button
        type="submit"
        onClick={() => {
          // Same reason as the docked bar below: `formActions` is rendered as a
          // sibling of `body` (line ~421), so this button sits OUTSIDE the
          // <form>. A `type="submit"` with no form ancestor is inert, which
          // left the desktop primary CTA completely dead — a wish could not
          // be created from /wishes/new on desktop at all. Drive submission
          // through the form element instead of duplicating the mutation.
          // Regression guard: wish-new-submit.test.tsx.
          formRef.current?.requestSubmit();
        }}
        disabled={createMut.isPending}
        data-testid="wish-new-submit"
        className="press inline-flex items-center gap-1.5 min-h-11 px-s-4 rounded-full bg-ink-1 text-paper text-body-sm font-semibold disabled:opacity-50"
      >
        <Sparkles className="h-4 w-4" aria-hidden />
        {createMut.isPending ? "建立中…" : "建立心願"}
      </button>
    </div>
  );

  const dockedActions = (
    <PageBottomActionBar
      primary={{
        label: createMut.isPending ? "建立中…" : "建立心願",
        onClick: () => {
          // The bar's button lives outside the <form>, so drive submission
          // through the form element itself rather than duplicating the
          // create mutation.
          formRef.current?.requestSubmit();
        },
        loading: createMut.isPending,
        leadingIcon: <Sparkles size={18} aria-hidden />,
        testId: "wish-new-submit",
      }}
      back={{
        label: "返回心願板",
        onClick: () => navigate("/wishes"),
        testId: "wish-new-back",
      }}
      testId="wish-new-bottom-bar"
    />
  );

  if (isMobile) {
    // Per spec §8.2: mobile uses full-screen BottomSheet (large detent) with
    // drag handle on top. We render inline in the page container because the
    // route itself is full-screen (mobile path) — the spec's "BottomSheet"
    // here means the page-anchored form, not a Radix dialog overlay (that
    // would block the back gesture and conflict with iOS sheet physics).
    return (
      <PageErrorBoundary label="新增心願">
        {/*
          W49.0 §6 — hero outside `PageContainer`; see `Wishes.tsx`.

          W49.0 §7 — and `pb-40` on this root, which is the part with teeth.
          The bar's real height is `pt-3` (12) + `h-11` (44) +
          `max(env(safe-area-inset-bottom), 12px)` = **68px**, or 90px on a
          phone with a home indicator. What this page had instead was
          `PageContainer`'s `py-s-5` (20) plus `MobileShell`'s `pb-8` (32) =
          **52px** — so the docked bar covered the last 16–38px of the form, on
          the one page in the app whose entire job is that form. `pb-40` is
          what `EventForm.tsx` and `anniversaries/new.tsx` already do.
        */}
        <div className="flex flex-col pb-40" data-testid="wish-new-mobile-page">
          <PageHero
            title="新增心願"
            // Per W35.5 spec §2.1: PageHero eyebrow must be meaningful on BOTH
            // viewports — never a literal "手機" / "desktop" placeholder
            // (W35.4 Bug #2 was the same shape on /wishes). Align with the
            // desktop path ("建立") so the chrome is consistent.
            eyebrow="建立"
            subtitle="填寫後會自動按類別預設區塊，可到 layout 編輯器調整"
            trailing={
              <button
                type="button"
                onClick={() => navigate("/wishes")}
                aria-label="返回心願板"
                data-testid="wish-new-mobile-back"
                // Borderless: the hero already sits on the page ground, and a
                // second outlined pill in the same row competed with the one
                // thing the row is for. `min-w-11` beside `min-h-11` keeps the
                // target square — it measured 42×44 without it (W46.0).
                className="press inline-flex h-11 w-11 items-center justify-center rounded-full text-ink-2 hover:bg-black/5"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
              </button>
            }
          />
          <PageContainer vertical="loose">
            {/* No card frame. `SectionStack` is the same container
                `EventForm.tsx:236` and `anniversaries/new.tsx:107` use for the
                same shape of form — a border + radius + shadow around the only
                section on a page draws a box the design has nowhere for. */}
            <SectionStack density="default">{body}</SectionStack>
            {dockedActions}
          </PageContainer>
        </div>
      </PageErrorBoundary>
    );
  }

  return (
    <PageErrorBoundary label="新增心願">
      {/* W49.0 §6 — hero outside `PageContainer`; see `Wishes.tsx`. */}
      <div className="flex flex-col" data-testid="wish-new-page">
        <PageHero
          title="新增心願"
          eyebrow="建立"
          subtitle="填寫後會自動按類別預設區塊，可到 layout 編輯器調整"
          trailing={
            <button
              type="button"
              onClick={() => navigate("/wishes")}
              data-testid="wish-new-back"
              className="press inline-flex items-center gap-1.5 min-h-11 px-s-4 rounded-full border border-hairline text-ink-1"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              返回心願板
            </button>
          }
        />
        <PageContainer>
          <div className="mx-auto max-w-[520px] surface-plain border border-hairline rounded-2xl p-s-5 shadow-card">
            {body}
            {formActions}
          </div>
        </PageContainer>
      </div>
    </PageErrorBoundary>
  );
}
